/**
 * State Reconciliation for WebSocket Notifications
 * 
 * Phase 3: State Management
 * 
 * Features:
 * - Reconcile local and server state
 * - Resolve conflicts
 * - Merge strategies
 * - Optimistic updates
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Merge strategies for conflict resolution
 */
export const MergeStrategy = {
  SERVER_WINS: 'SERVER_WINS',       // Server data takes precedence
  CLIENT_WINS: 'CLIENT_WINS',       // Client data takes precedence
  MERGE_NEWEST: 'MERGE_NEWEST',     // Newest data wins (by timestamp)
  MERGE_UNION: 'MERGE_UNION',       // Union of both (no duplicates)
  MERGE_CUSTOM: 'MERGE_CUSTOM'      // Custom merge function
};

/**
 * Conflict types
 */
export const ConflictType = {
  NOTIFICATION_MISMATCH: 'NOTIFICATION_MISMATCH',
  COUNT_MISMATCH: 'COUNT_MISMATCH',
  STATE_MISMATCH: 'STATE_MISMATCH',
  TIMESTAMP_MISMATCH: 'TIMESTAMP_MISMATCH'
};

/**
 * State Reconciliation Manager
 */
export class StateReconciliationManager {
  constructor(options = {}) {
    this.defaultStrategy = options.defaultStrategy || MergeStrategy.MERGE_NEWEST;
    this.enableOptimisticUpdates = options.enableOptimisticUpdates !== false;
    this.conflictResolutionTimeout = options.conflictResolutionTimeout || 5000; // 5 seconds
    
    // Statistics
    this.stats = {
      totalReconciliations: 0,
      totalConflicts: 0,
      conflictsByType: {},
      resolvedConflicts: 0,
      failedReconciliations: 0,
      optimisticUpdates: 0,
      optimisticUpdateReverts: 0
    };
    
    // Optimistic update tracking
    this.optimisticUpdates = new Map();
    
    console.log('🔄 State reconciliation manager initialized');
  }
  
  /**
   * Reconcile local and server state
   */
  reconcile(localState, serverState, strategy = null) {
    this.stats.totalReconciliations++;
    
    const mergeStrategy = strategy || this.defaultStrategy;
    
    console.log(`🔄 Reconciling state with strategy: ${mergeStrategy}`);
    
    try {
      // Detect conflicts
      const conflicts = this.detectConflicts(localState, serverState);
      
      if (conflicts.length > 0) {
        console.log(`⚠️ Detected ${conflicts.length} conflicts`);
        this.stats.totalConflicts += conflicts.length;
        
        // Track conflicts by type
        conflicts.forEach(conflict => {
          this.stats.conflictsByType[conflict.type] = 
            (this.stats.conflictsByType[conflict.type] || 0) + 1;
        });
      }
      
      // Resolve conflicts and merge
      const reconciledState = this.mergeStates(
        localState,
        serverState,
        conflicts,
        mergeStrategy
      );
      
      this.stats.resolvedConflicts += conflicts.length;
      
      console.log('✅ State reconciliation complete');
      
      return {
        success: true,
        state: reconciledState,
        conflicts: conflicts,
        strategy: mergeStrategy
      };
    } catch (error) {
      console.error('❌ State reconciliation failed:', error);
      this.stats.failedReconciliations++;
      
      return {
        success: false,
        error: error,
        state: serverState // Fallback to server state
      };
    }
  }
  
  /**
   * Detect conflicts between local and server state
   */
  detectConflicts(localState, serverState) {
    const conflicts = [];
    
    // Check notifications mismatch
    if (localState.notifications && serverState.notifications) {
      const localIds = new Set(localState.notifications.map(n => n.id));
      const serverIds = new Set(serverState.notifications.map(n => n.id));
      
      // Find notifications only in local
      const onlyLocal = localState.notifications.filter(n => !serverIds.has(n.id));
      
      // Find notifications only in server
      const onlyServer = serverState.notifications.filter(n => !localIds.has(n.id));
      
      if (onlyLocal.length > 0 || onlyServer.length > 0) {
        conflicts.push({
          type: ConflictType.NOTIFICATION_MISMATCH,
          field: 'notifications',
          localValue: localState.notifications,
          serverValue: serverState.notifications,
          onlyLocal: onlyLocal,
          onlyServer: onlyServer
        });
      }
    }
    
    // Check unread count mismatch
    if (localState.unreadCount !== undefined && 
        serverState.unreadCount !== undefined &&
        localState.unreadCount !== serverState.unreadCount) {
      conflicts.push({
        type: ConflictType.COUNT_MISMATCH,
        field: 'unreadCount',
        localValue: localState.unreadCount,
        serverValue: serverState.unreadCount
      });
    }
    
    // Check timestamp mismatch
    if (localState.timestamp && serverState.timestamp) {
      const timeDiff = Math.abs(localState.timestamp - serverState.timestamp);
      
      // If difference is more than 5 minutes, it's a conflict
      if (timeDiff > 5 * 60 * 1000) {
        conflicts.push({
          type: ConflictType.TIMESTAMP_MISMATCH,
          field: 'timestamp',
          localValue: localState.timestamp,
          serverValue: serverState.timestamp,
          difference: timeDiff
        });
      }
    }
    
    return conflicts;
  }
  
  /**
   * Merge states based on strategy
   */
  mergeStates(localState, serverState, conflicts, strategy) {
    switch (strategy) {
      case MergeStrategy.SERVER_WINS:
        return this.mergeServerWins(serverState);
      
      case MergeStrategy.CLIENT_WINS:
        return this.mergeClientWins(localState);
      
      case MergeStrategy.MERGE_NEWEST:
        return this.mergeNewest(localState, serverState);
      
      case MergeStrategy.MERGE_UNION:
        return this.mergeUnion(localState, serverState);
      
      default:
        console.warn(`⚠️ Unknown merge strategy: ${strategy}, using SERVER_WINS`);
        return this.mergeServerWins(serverState);
    }
  }
  
  /**
   * Server wins strategy - server data takes precedence
   */
  mergeServerWins(serverState) {
    console.log('🔄 Merge strategy: SERVER_WINS');
    return { ...serverState };
  }
  
  /**
   * Client wins strategy - client data takes precedence
   */
  mergeClientWins(localState) {
    console.log('🔄 Merge strategy: CLIENT_WINS');
    return { ...localState };
  }
  
  /**
   * Merge newest strategy - newest data wins by timestamp
   */
  mergeNewest(localState, serverState) {
    console.log('🔄 Merge strategy: MERGE_NEWEST');
    
    const localTime = localState.timestamp || 0;
    const serverTime = serverState.timestamp || 0;
    
    if (serverTime >= localTime) {
      return { ...serverState };
    } else {
      return { ...localState };
    }
  }
  
  /**
   * Merge union strategy - union of both without duplicates
   */
  mergeUnion(localState, serverState) {
    console.log('🔄 Merge strategy: MERGE_UNION');
    
    const merged = { ...serverState };
    
    // Merge notifications (union without duplicates)
    if (localState.notifications && serverState.notifications) {
      const notificationMap = new Map();
      
      // Add server notifications first
      serverState.notifications.forEach(n => {
        notificationMap.set(n.id, n);
      });
      
      // Add local notifications (newer ones override)
      localState.notifications.forEach(n => {
        const existing = notificationMap.get(n.id);
        
        if (!existing || (n.timestamp && existing.timestamp && n.timestamp > existing.timestamp)) {
          notificationMap.set(n.id, n);
        }
      });
      
      merged.notifications = Array.from(notificationMap.values());
      
      // Sort by timestamp (newest first)
      merged.notifications.sort((a, b) => {
        const aTime = a.timestamp || 0;
        const bTime = b.timestamp || 0;
        return bTime - aTime;
      });
    }
    
    // Use server unread count (more reliable)
    merged.unreadCount = serverState.unreadCount;
    
    // Use newest timestamp
    merged.timestamp = Math.max(
      localState.timestamp || 0,
      serverState.timestamp || 0
    );
    
    return merged;
  }
  
  /**
   * Apply optimistic update
   */
  applyOptimisticUpdate(updateId, updateFn, currentState) {
    if (!this.enableOptimisticUpdates) {
      console.log('⏭️ Optimistic updates disabled');
      return currentState;
    }
    
    console.log(`⚡ Applying optimistic update: ${updateId}`);
    
    // Store original state for potential revert
    this.optimisticUpdates.set(updateId, {
      originalState: { ...currentState },
      timestamp: Date.now()
    });
    
    // Apply update
    const updatedState = updateFn(currentState);
    
    this.stats.optimisticUpdates++;
    
    return updatedState;
  }
  
  /**
   * Confirm optimistic update (remove from tracking)
   */
  confirmOptimisticUpdate(updateId) {
    if (this.optimisticUpdates.has(updateId)) {
      this.optimisticUpdates.delete(updateId);
      console.log(`✅ Confirmed optimistic update: ${updateId}`);
      return true;
    }
    
    return false;
  }
  
  /**
   * Revert optimistic update
   */
  revertOptimisticUpdate(updateId) {
    const update = this.optimisticUpdates.get(updateId);
    
    if (!update) {
      console.warn(`⚠️ No optimistic update found to revert: ${updateId}`);
      return null;
    }
    
    console.log(`↩️ Reverting optimistic update: ${updateId}`);
    
    this.optimisticUpdates.delete(updateId);
    this.stats.optimisticUpdateReverts++;
    
    return update.originalState;
  }
  
  /**
   * Clear old optimistic updates (older than timeout)
   */
  clearOldOptimisticUpdates() {
    const now = Date.now();
    let cleared = 0;
    
    for (const [updateId, update] of this.optimisticUpdates.entries()) {
      const age = now - update.timestamp;
      
      if (age > this.conflictResolutionTimeout) {
        this.optimisticUpdates.delete(updateId);
        cleared++;
      }
    }
    
    if (cleared > 0) {
      console.log(`🗑️ Cleared ${cleared} old optimistic updates`);
    }
  }
  
  /**
   * Get statistics
   */
  getStats() {
    return {
      ...this.stats,
      pendingOptimisticUpdates: this.optimisticUpdates.size,
      optimisticUpdatesEnabled: this.enableOptimisticUpdates
    };
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalReconciliations: 0,
      totalConflicts: 0,
      conflictsByType: {},
      resolvedConflicts: 0,
      failedReconciliations: 0,
      optimisticUpdates: 0,
      optimisticUpdateReverts: 0
    };
    
    console.log('📊 Reset state reconciliation statistics');
  }
}

export default StateReconciliationManager;

